import { useState } from 'react';

const SECTIONS = [
  {
    id: '1', title: 'Information We Collect',
    items: [
      { sub: '1.1 Information You Provide', text: 'When you create an account, we collect personal information such as your name, email address, date of birth, and profile photo. For your football profile, you may optionally provide your position, height, weight, preferred foot, and performance statistics. You may also upload media content such as photos and video highlights.' },
      { sub: '1.2 Location Information', text: 'To help you find games near you and connect with nearby players, we collect your device\'s approximate location. You can control location permissions through your device settings. Disabling location access may limit certain features of the application.' },
      { sub: '1.3 Usage Data', text: 'We automatically collect information about how you interact with the app, including pages visited, features used, games created or joined, session duration, device type, operating system, and app version. This data helps us improve the user experience.' },
      { sub: '1.4 Communications', text: 'When you contact us for support or interact with other users through the platform, we may retain those communications to help resolve disputes, improve customer support, and ensure platform safety.' },
    ],
  },
  {
    id: '2', title: 'How We Use Your Information',
    items: [
      { sub: '2.1 Providing the Service', text: 'We use your information to operate and provide the Hadafi platform, including creating and managing your account, facilitating game organization, connecting you with nearby players, processing in-app requests, and sending service-related notifications.' },
      { sub: '2.2 Improving Our Service', text: 'We analyze usage patterns and feedback to improve app functionality, fix bugs, develop new features, personalize your experience, and ensure platform stability and security.' },
      { sub: '2.3 Safety & Security', text: 'We use your information to detect and prevent fraud, abuse, and other harmful activities; verify user identity; enforce our Terms of Service; and protect the rights and safety of our users and third parties.' },
      { sub: '2.4 Communications', text: 'We may send you notifications about games, updates to our service, changes to our policies, and promotional materials about new features. You can opt out of promotional communications at any time through your account settings.' },
    ],
  },
  {
    id: '3', title: 'Information Sharing & Disclosure',
    items: [
      { sub: '3.1 Public Profile Information', text: 'Your player profile—including your name, position, profile photo, game stats, and highlights you choose to make public—is visible to other users of the platform. You control the privacy level of your profile through your account settings.' },
      { sub: '3.2 With Other Users', text: 'When you create or join games, your profile information is shared with other participants. In-app messaging is visible to the participants of those conversations.' },
      { sub: '3.3 Service Providers', text: 'We work with trusted third-party service providers to operate our platform, including cloud hosting providers, analytics services, and customer support tools. These providers are contractually bound to protect your information and may only use it to provide services to us.' },
      { sub: '3.4 Legal Requirements', text: 'We may disclose your information if required by law, court order, or governmental authority, or if we believe in good faith that disclosure is necessary to protect the rights, property, or safety of Hadafi, our users, or the public.' },
      { sub: '3.5 Business Transfers', text: 'In the event of a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you via email and/or prominent notice in the app before your information is transferred and becomes subject to a different privacy policy.' },
    ],
  },
  {
    id: '4', title: 'Data Retention',
    items: [
      { sub: '', text: 'We retain your personal information for as long as your account is active or as needed to provide you with services. You may request deletion of your account and associated data at any time through the app settings or by contacting us at privacy@hadafi.ae. We will process deletion requests within 30 days, except where we are required by law to retain certain information or where data is required for legitimate business purposes such as fraud prevention.' },
    ],
  },
  {
    id: '5', title: 'Your Rights & Choices',
    items: [
      { sub: '5.1 Access & Correction', text: 'You may access, update, or correct your personal information at any time through your account settings within the app.' },
      { sub: '5.2 Data Deletion', text: 'You may request deletion of your account and all associated personal data. Certain data may be retained as required by law or for legitimate business purposes.' },
      { sub: '5.3 Data Portability', text: 'You may request a copy of your personal data in a structured, machine-readable format by contacting us at privacy@hadafi.ae.' },
      { sub: '5.4 Opt-Out of Communications', text: 'You may opt out of marketing and promotional emails by clicking the unsubscribe link in any email, or by updating your notification preferences in the app.' },
      { sub: '5.5 Location Data', text: 'You may disable location permissions through your device settings at any time. Note that some location-based features will not be available if location access is disabled.' },
    ],
  },
  {
    id: '6', title: "Children's Privacy",
    items: [
      { sub: '', text: "Hadafi is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child under 13 has provided us with personal information, please contact us at privacy@hadafi.ae. We will take steps to delete such information from our servers promptly. Users between the ages of 13 and 18 may only use Hadafi with the consent of a parent or legal guardian." },
    ],
  },
  {
    id: '7', title: 'Security',
    items: [
      { sub: '', text: 'We implement industry-standard security measures to protect your personal information, including encryption of data in transit using TLS/SSL, secure storage practices, regular security assessments, and access controls that limit who can access your data. However, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security, and you use the service at your own risk. Please immediately notify us at security@hadafi.ae if you suspect any unauthorized access to your account.' },
    ],
  },
  {
    id: '8', title: 'Third-Party Services',
    items: [
      { sub: '', text: 'Our app may contain links to third-party websites, or integrate with third-party services such as map providers for location features. This Privacy Policy does not apply to those third-party services. We encourage you to review the privacy policies of any third-party services you access through Hadafi. We are not responsible for the privacy practices of third-party services.' },
    ],
  },
  {
    id: '9', title: 'Cookies & Tracking Technologies',
    items: [
      { sub: '', text: 'Our mobile application does not use traditional web cookies. However, we use similar tracking technologies such as device identifiers and analytics SDKs to understand how users interact with our app, improve performance, and provide relevant features. You can reset your advertising identifier through your device settings. Our app does not serve advertisements, and we do not share your data with advertising networks.' },
    ],
  },
  {
    id: '10', title: 'International Data Transfers',
    items: [
      { sub: '', text: 'Hadafi operates in the United Arab Emirates. Your information may be transferred to and processed in countries other than the UAE, including where our service providers operate. These countries may have data protection laws that differ from those in the UAE. By using Hadafi, you consent to such transfers. We take steps to ensure that any international transfers comply with applicable data protection laws and that your information remains protected.' },
    ],
  },
  {
    id: '11', title: 'Changes to This Policy',
    items: [
      { sub: '', text: "We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new policy within the app and updating the 'Last Updated' date. Your continued use of the app after the effective date of the revised Privacy Policy constitutes your acceptance of the updated terms. We encourage you to periodically review this policy." },
    ],
  },
  {
    id: '12', title: 'Contact Us',
    items: [
      { sub: '', text: 'If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:' },
      { sub: 'Hadafi – Privacy Team', text: 'Email: privacy@hadafi.ae\nWebsite: hadafi.ae\nAddress: Dubai, United Arab Emirates\n\nWe will respond to all inquiries within 30 days.' },
    ],
  },
];

const QUICK_SUMMARY = [
  ['We don\'t sell your data',              'Never have, never will.'],
  ['We don\'t serve ads',                   'Hadafi is completely ad-free.'],
  ['You control your data',                 'Delete your account anytime.'],
  ['Location is optional',                  'But improves your experience.'],
  ['Industry-standard security',            'Your data is encrypted in transit.'],
];

export default function PrivacyPage() {
  const [active, setActive] = useState('1');

  const scrollTo = (id) => {
    setActive(id);
    document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ paddingTop: 68 }}>

      {/* ── HEADER ── */}
      <section style={{
        padding: '5rem clamp(1rem,4vw,3rem) 3rem',
        background: 'var(--navy2)',
        borderBottom: '1px solid rgba(232,96,74,0.1)',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <span className="section-tag">LEGAL</span>
          <h1 className="section-title" style={{ fontSize: 'clamp(42px,6vw,68px)', marginBottom: '1rem' }}>
            Privacy &amp;<br /><span>Policy</span>
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: 14, fontFamily: 'var(--font-m)', marginBottom: '1.5rem' }}>
            Last Updated: {new Date().toLocaleDateString('en-AE', { year: 'numeric', month: 'long', day: 'numeric' })}
            &nbsp;•&nbsp; Version 1.0
          </p>

          {/* Intro box */}
          <div style={{
            padding: '1.25rem', borderRadius: 4, maxWidth: 680,
            background: 'rgba(232,96,74,0.08)',
            border: '1px solid rgba(232,96,74,0.2)',
          }}>
            <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7 }}>
              This Privacy Policy describes how Hadafi ("we," "us," or "our") collects, uses, and shares information about you when you use our mobile application and related services (the "Service"). By using the Service, you agree to the collection and use of information in accordance with this policy.
            </p>
          </div>
        </div>
      </section>

      {/* ── BODY ── */}
      <section style={{ padding: '3rem clamp(1rem,4vw,3rem) 5rem' }}>
        <div style={{
          maxWidth: 900, margin: '0 auto',
          display: 'grid', gridTemplateColumns: '200px 1fr',
          gap: '3rem', alignItems: 'start',
        }}>

          {/* TOC */}
          <div style={{ position: 'sticky', top: 88 }}>
            <p style={{ fontFamily: 'var(--font-m)', fontSize: 10, color: 'var(--muted)', letterSpacing: 2, marginBottom: '1rem', textTransform: 'uppercase' }}>Contents</p>
            {SECTIONS.map((s) => (
              <button key={s.id} onClick={() => scrollTo(s.id)} style={{
                display: 'block', width: '100%', textAlign: 'left',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '7px 0 7px 12px',
                borderLeft: active === s.id ? '2px solid var(--coral)' : '2px solid transparent',
                fontFamily: 'var(--font-b)', fontSize: 12,
                color: active === s.id ? 'var(--coral)' : 'var(--muted)',
                lineHeight: 1.4, transition: 'color 0.2s, border-color 0.2s',
              }}>{s.id}. {s.title}</button>
            ))}
          </div>

          {/* Content */}
          <div>
            {SECTIONS.map((s) => (
              <div key={s.id} id={`section-${s.id}`} style={{ marginBottom: '3rem', scrollMarginTop: 90 }}>
                <h2 style={{
                  fontFamily: 'var(--font-d)', fontSize: 26, fontWeight: 800,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  paddingBottom: '0.75rem', marginBottom: '1.5rem',
                  borderBottom: '1px solid rgba(232,96,74,0.2)',
                }}>{s.id}. {s.title}</h2>

                {s.items.map((item, i) => (
                  <div key={i} style={{ marginBottom: '1.5rem' }}>
                    {item.sub && (
                      <h3 style={{
                        fontFamily: 'var(--font-d)', fontSize: 16, fontWeight: 700,
                        color: 'var(--coral)', letterSpacing: 1, textTransform: 'uppercase',
                        marginBottom: '0.5rem',
                      }}>{item.sub}</h3>
                    )}
                    {item.text.split('\n').map((line, li) => (
                      <p key={li} style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.8, marginBottom: line ? '0.25rem' : '0.5rem' }}>
                        {line}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            ))}

            {/* Quick summary */}
            <div style={{
              padding: '2rem',
              background: 'var(--navy2)',
              border: '1px solid rgba(232,96,74,0.15)',
              borderRadius: 8, marginTop: '2rem',
            }}>
              <h3 style={{ fontFamily: 'var(--font-d)', fontSize: 20, fontWeight: 800, textTransform: 'uppercase', marginBottom: '1.25rem' }}>Quick Summary</h3>
              {QUICK_SUMMARY.map(([title, desc], i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <span style={{ color: 'var(--coral)', fontSize: 14, flexShrink: 0 }}>✓</span>
                  <span style={{ color: 'var(--muted)', fontSize: 14 }}>
                    <strong style={{ color: 'var(--white)' }}>{title}</strong> — {desc}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
